import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import React, { forwardRef } from "react";
import { cn } from "../../../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "bg-[linear-gradient(135deg,#788AF7_0%,#3551F3_100%)] text-white border-none shadow-sm hover:opacity-80 hover:shadow-md active:from-indigo-500 active:to-indigo-700 active:shadow-sm active:translate-y-px",
        "outline-primary":
          "text-[#3551F3] border border-[#3551F3] hover:bg-[#3551F3] hover:text-white",
        secondary:
          "bg-indigo-50 text-indigo-500 border border-indigo-500 hover:border-indigo-700 hover:bg-gradient-to-b hover:from-indigo-100/50 hover:to-indigo-100/50 active:border-indigo-700",
        outline:
          "border border-gray-200 bg-background hover:bg-accent hover:text-accent-foreground",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        link: "text-primary underline-offset-4 hover:underline",
        ai: "bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100",
        gradient:
          "bg-gradient-to-b from-indigo-300 to-indigo-500 text-white border-none shadow-sm hover:from-indigo-400 hover:to-indigo-600 hover:shadow-md active:from-indigo-500 active:to-indigo-700",
        redirect:
          "bg-indigo-50 text-indigo-500 border border-indigo-500 hover:border-indigo-700 hover:bg-gradient-to-b hover:from-indigo-100/50 hover:to-indigo-100/50 shadow-sm",
        "ai-gradient":
          "text-white bg-[linear-gradient(93deg,#3551F3_0%,#4B6EE3_45.06%,#00B5E2_100%)] hover:opacity-90",
        text: "bg-transparent border-none text-[#374151] font-sans font-medium text-xs leading-[1rem] tracking-[0%] align-middle active:opacity-70",
      },
      size: {
        sm: "h-8 px-3 py-1.5 text-xs gap-1.5",
        md: "h-9 px-4 py-2 text-sm gap-2",
        lg: "h-11 px-6 py-3 text-base gap-2.5",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

export interface ButtonV2Props
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "prefix">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  loading?: boolean;
  className?: string;
  textClassName?: string;
  prefixClassName?: string;
  suffixClassName?: string;
  children?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const LoadingSpinner: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={cn("animate-spin h-4 w-4", className)}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export const ButtonV2 = forwardRef<HTMLButtonElement, ButtonV2Props>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      prefix,
      suffix,
      loading = false,
      textClassName,
      prefixClassName,
      suffixClassName,
      disabled,
      children,
      type = "button",
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const isDisabled = disabled || loading;

    if (asChild) {
      return (
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          disabled={isDisabled}
          type={type}
          {...props}
        />
      );
    }

    const isIconOnly = size === "icon" && !prefix && !suffix && children;

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={isDisabled}
        type={type}
        {...props}
      >
        {loading ? (
          <LoadingSpinner className={cn("flex-shrink-0", prefixClassName)} />
        ) : prefix ? (
          <span
            className={cn(
              "flex items-center justify-center flex-shrink-0",
              prefixClassName
            )}
          >
            {prefix}
          </span>
        ) : null}

        {children && (
          <span
            className={cn(isIconOnly ? "" : "flex items-center", textClassName)}
          >
            {children}
          </span>
        )}

        {suffix && !loading && (
          <span
            className={cn(
              "flex items-center justify-center flex-shrink-0",
              suffixClassName
            )}
          >
            {suffix}
          </span>
        )}
      </Comp>
    );
  }
);

ButtonV2.displayName = "ButtonV2";

export { buttonVariants };
export default ButtonV2;
